import React, {type PropsWithChildren, useCallback, useRef} from "react";

type Value = string | number | null;

function defaultGetValue(el: HTMLElement): Value {
  // 1) „echte“ Formelemente
  if ("value" in el && typeof (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value === "string") {
    return (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
  }
  // 2) data-value
  const dv = el.getAttribute("data-value");
  if (dv != null) return dv;
  // 3) aria-checked/selected für Radios/Options
  const selected = el.getAttribute("aria-selected");
  if (selected === "true") return el.getAttribute("id") ?? el.textContent ?? null;
  // 4) Fallback: Textinhalt (nicht perfekt, aber praktikabel)
  const txt = el.textContent?.trim();
  return txt && txt.length ? txt : null;
}

type FocusRoverProps = PropsWithChildren & {
  itemSelector: string,
  tabIndex?: number,
  activeValue?: Value,
  getValue?: (el: HTMLElement) => Value;
  onItemFocus?: (el: HTMLElement, index: number) => void;
}

export function FocusRover({children, tabIndex = 0, itemSelector, activeValue = null, getValue = defaultGetValue} : FocusRoverProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const getItems = useCallback((): HTMLElement[] => {
    const root = wrapRef.current;
    if (!root) return [];
    const nodeList = root.querySelectorAll<HTMLElement>(itemSelector);
    // Nur fokussierbare & enabled Items nehmen
    return Array.from(nodeList);
  }, [itemSelector]);

  const indexOfActiveValue = useCallback(() => {
    if (activeValue == null) return 0;
    const items = getItems();
    const idx = items.findIndex((el) => {
      const v = getValue(el);
      return v != null && String(v) === String(activeValue);
    });
    return idx >= 0 ? idx : 0;
  }, [activeValue, getItems, getValue]);

  const focusItemAt = useCallback((index: number) => {
    const items = getItems();
    if (!items.length) return;
    const clamped = Math.max(0, Math.min(index, items.length - 1));
    const el = items[clamped];
    // roving tabindex pflegen
    el.focus({ preventScroll: false });
  }, [getItems]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget !== e.target) return; // Bubble von Kind ignorieren
    const idx = indexOfActiveValue();
    focusItemAt(idx);
  }, [focusItemAt, indexOfActiveValue]);

  return(
      <div tabIndex={tabIndex}
           ref={wrapRef}
           onFocus={handleFocus}
           >
        {children}
      </div>
  )
}